import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, BadgeCheck, Sparkles, MessageCircle, Clock } from "lucide-react";

const WHATSAPP_URL =
  process.env.REACT_APP_WHATSAPP_URL ||
  "https://chat.whatsapp.com/PULLSTATION-INVITE"; // sostituisci con il tuo invite ufficiale

const CATS = [
  { slug: "op-preorder-jp", title: "One Piece Preorder JP", tag: "TCG-01",
    desc: "Preorder giapponesi • 15-20 giorni lavorativi", tone: "op" },
  { slug: "op-preorder-en", title: "One Piece Preorder EN", tag: "TCG-02",
    desc: "Su allocazione • acconto + saldo alla conferma", tone: "op" },
  { slug: "singles", title: "Carte Singole", tag: "TCG-03",
    desc: "One Piece JP/EN • Pokémon JP", tone: "singles" },
  { slug: "pkm-sealed-it", title: "Pokémon Sealed IT", tag: "TCG-04",
    desc: "Prodotti Pokémon italiani sigillati", tone: "pkm" },
  { slug: "pkm-sealed-jp", title: "Pokémon Sealed JP", tag: "TCG-05",
    desc: "Import giapponese • 15-20 giorni lavorativi", tone: "pkm" },
];

export default function Home() {
  return (
    <div className="text-white">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#C9A227]/20">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow flex items-center gap-3">
                <span className="w-8 h-px bg-[#C9A227]" /> One Piece • Pokémon • TCG
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[0.95]">
                <span className="block text-white">PULL</span>
                <span className="block text-[#C9A227] italic">STATION</span>
              </h1>
              <div className="gold-hairline my-6 max-w-md" />
              <p className="text-lg md:text-xl text-[#D1D5DB] max-w-xl leading-relaxed">
                Il tuo punto di riferimento per <span className="text-white font-semibold">carte, sealed products, preorder</span> e prodotti da collezione.
                <span className="block mt-2 text-[#9CA3AF] text-base">Trova. Acquista. Colleziona.</span>
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <a
                  href="#sezioni"
                  data-testid="hero-cta-sections"
                  className="btn-gold"
                >
                  Esplora le sezioni <ArrowRight size={16} />
                </a>
                <Link data-testid="hero-cta-richieste" to="/richieste" className="btn-ghost-gold">
                  Richieste prodotti
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                <Trust icon={<BadgeCheck size={16} />} label="Autenticità garantita" />
                <Trust icon={<Truck size={16} />} label="Spedizione protetta" />
                <Trust icon={<ShieldCheck size={16} />} label="Pagamenti sicuri" />
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] max-w-md mx-auto">
                {/* Ambient gold glow behind the composition */}
                <div className="absolute -inset-8 rounded-full opacity-70 blur-3xl pointer-events-none"
                     style={{ background: "radial-gradient(closest-side, rgba(201,162,39,0.35), transparent 70%)" }} />

                {/* Tilted card outlines behind */}
                <div className="absolute inset-0 border border-[#C9A227]/25 rotate-6" />
                <div className="absolute inset-0 border border-[#C9A227]/40 -rotate-3" />

                <div className="absolute inset-2 bg-[#0b0b0d] border border-[#C9A227] overflow-hidden ps-hero-sheen">
                  <svg viewBox="0 0 400 500" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
                    <defs>
                      <linearGradient id="hero-gold" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#F3D068" />
                        <stop offset="1" stopColor="#8B6C15" />
                      </linearGradient>
                      <linearGradient id="hero-foil" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#F3D068" stopOpacity="0.35" />
                        <stop offset="0.45" stopColor="#F3D068" stopOpacity="0.9" />
                        <stop offset="0.55" stopColor="#FFFFFF" stopOpacity="0.6" />
                        <stop offset="0.7" stopColor="#F3D068" stopOpacity="0.9" />
                        <stop offset="1" stopColor="#8B6C15" stopOpacity="0.35" />
                      </linearGradient>
                      <radialGradient id="hero-burst" cx="0.5" cy="0.5" r="0.55">
                        <stop offset="0" stopColor="#F3D068" stopOpacity="0.55" />
                        <stop offset="0.35" stopColor="#C9A227" stopOpacity="0.18" />
                        <stop offset="1" stopColor="#0b0b0d" stopOpacity="0" />
                      </radialGradient>
                      <pattern id="hero-grid-p" width="24" height="24" patternUnits="userSpaceOnUse">
                        <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#C9A227" strokeOpacity="0.06" strokeWidth="0.5" />
                      </pattern>
                    </defs>

                    {/* Base grid */}
                    <rect width="400" height="500" fill="url(#hero-grid-p)" />
                    {/* Radial burst behind main card */}
                    <circle cx="200" cy="250" r="220" fill="url(#hero-burst)" className="ps-pulse" />

                    {/* Light rays from center */}
                    <g stroke="url(#hero-gold)" strokeWidth="0.8" opacity="0.55">
                      <line x1="200" y1="250" x2="40" y2="80" />
                      <line x1="200" y1="250" x2="360" y2="80" />
                      <line x1="200" y1="250" x2="20" y2="230" />
                      <line x1="200" y1="250" x2="380" y2="230" />
                      <line x1="200" y1="250" x2="70" y2="470" />
                      <line x1="200" y1="250" x2="330" y2="470" />
                    </g>
                    <g stroke="#F3D068" strokeWidth="0.4" opacity="0.3">
                      <line x1="200" y1="250" x2="120" y2="30" />
                      <line x1="200" y1="250" x2="280" y2="30" />
                      <line x1="200" y1="250" x2="10" y2="330" />
                      <line x1="200" y1="250" x2="390" y2="330" />
                    </g>

                    {/* ============ BACK CARDS FAN ============ */}
                    <g transform="translate(60 140) rotate(-22)">
                      <rect x="0" y="0" width="120" height="170" rx="4"
                            fill="#0f0f11" stroke="url(#hero-gold)" strokeWidth="1" opacity="0.85" />
                      <rect x="6" y="6" width="108" height="158" rx="3"
                            fill="none" stroke="#C9A227" strokeWidth="0.5" opacity="0.5" />
                      <polygon points="60,50 90,68 90,102 60,120 30,102 30,68"
                               fill="none" stroke="url(#hero-gold)" strokeWidth="0.9" opacity="0.7" />
                    </g>

                    <g transform="translate(90 165) rotate(-12)">
                      <rect x="0" y="0" width="130" height="185" rx="5"
                            fill="#141416" stroke="url(#hero-gold)" strokeWidth="1.1" />
                      <rect x="6" y="6" width="118" height="173" rx="4"
                            fill="none" stroke="#C9A227" strokeWidth="0.6" opacity="0.6" />
                      <polygon points="65,58 96,78 96,116 65,136 34,116 34,78"
                               fill="none" stroke="url(#hero-gold)" strokeWidth="1.1" />
                      <text x="65" y="105" textAnchor="middle" fill="url(#hero-gold)"
                            fontFamily="Outfit, sans-serif" fontWeight="900" fontSize="22" letterSpacing="1">PS</text>
                      <rect x="14" y="150" width="102" height="18" fill="#000" fillOpacity="0.6" />
                      <text x="20" y="163" fill="#F3D068" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="2">OP-09 · JP</text>
                    </g>

                    <g transform="translate(180 165) rotate(12)">
                      <rect x="0" y="0" width="130" height="185" rx="5"
                            fill="#141416" stroke="url(#hero-gold)" strokeWidth="1.1" />
                      <rect x="6" y="6" width="118" height="173" rx="4"
                            fill="none" stroke="#C9A227" strokeWidth="0.6" opacity="0.6" />
                      <polygon points="65,58 96,78 96,116 65,136 34,116 34,78"
                               fill="none" stroke="url(#hero-gold)" strokeWidth="1.1" />
                      <text x="65" y="105" textAnchor="middle" fill="url(#hero-gold)"
                            fontFamily="Outfit, sans-serif" fontWeight="900" fontSize="22" letterSpacing="1">PS</text>
                      <rect x="14" y="150" width="102" height="18" fill="#000" fillOpacity="0.6" />
                      <text x="20" y="163" fill="#F3D068" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="2">SV 151 · EN</text>
                    </g>

                    <g transform="translate(220 140) rotate(22)">
                      <rect x="0" y="0" width="120" height="170" rx="4"
                            fill="#0f0f11" stroke="url(#hero-gold)" strokeWidth="1" opacity="0.85" />
                      <rect x="6" y="6" width="108" height="158" rx="3"
                            fill="none" stroke="#C9A227" strokeWidth="0.5" opacity="0.5" />
                      <polygon points="60,50 90,68 90,102 60,120 30,102 30,68"
                               fill="none" stroke="url(#hero-gold)" strokeWidth="0.9" opacity="0.7" />
                    </g>

                    {/* ============ HERO CARD (center, foreground) ============ */}
                    <g transform="translate(130 130)">
                      <rect x="0" y="0" width="140" height="220" rx="6"
                            fill="#0b0b0d" stroke="url(#hero-foil)" strokeWidth="2.2" />
                      <rect x="4" y="4" width="132" height="212" rx="5"
                            fill="none" stroke="#F3D068" strokeWidth="0.7" opacity="0.85" />
                      <rect x="8" y="8" width="124" height="204" rx="4"
                            fill="none" stroke="#C9A227" strokeWidth="0.4" opacity="0.6" />

                      <text x="14" y="22" fill="#C9A227" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="2.5">PULL STATION</text>
                      <text x="126" y="22" textAnchor="end" fill="#F3D068" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="2">★★★</text>

                      <polygon points="70,50 108,72 108,120 70,142 32,120 32,72"
                               fill="none" stroke="url(#hero-foil)" strokeWidth="1.4" />
                      <polygon points="70,64 96,80 96,114 70,130 44,114 44,80"
                               fill="none" stroke="#F3D068" strokeWidth="0.7" opacity="0.7" />
                      <g stroke="#F3D068" strokeWidth="0.6" opacity="0.55">
                        <line x1="70" y1="96" x2="70" y2="60" />
                        <line x1="70" y1="96" x2="70" y2="132" />
                        <line x1="70" y1="96" x2="38" y2="96" />
                        <line x1="70" y1="96" x2="102" y2="96" />
                      </g>

                      <text x="70" y="106" textAnchor="middle" fill="url(#hero-foil)"
                            fontFamily="Outfit, sans-serif" fontWeight="900" fontSize="36" letterSpacing="1">PS</text>

                      <rect x="12" y="160" width="116" height="30" fill="#000" fillOpacity="0.55" />
                      <text x="18" y="173" fill="#F3D068" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="2.4">RARE ▸ HOLO</text>
                      <text x="18" y="184" fill="#D1D5DB" fontFamily="JetBrains Mono, monospace" fontSize="6.5" letterSpacing="1.5">ONE PIECE · POKÉMON</text>
                      <text x="122" y="184" textAnchor="end" fill="#F3D068" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="1">001/165</text>

                      <line x1="12" y1="200" x2="128" y2="200" stroke="#C9A227" strokeWidth="0.4" opacity="0.6" />
                      <text x="70" y="211" textAnchor="middle" fill="#C9A227" fontFamily="JetBrains Mono, monospace" fontSize="6.5" letterSpacing="3">CERTIFIED · AUTHENTIC</text>

                      <polygon points="10,0 40,0 -20,220 -50,220" fill="#FFFFFF" opacity="0.05" />
                      <polygon points="95,0 115,0 65,220 45,220" fill="#F3D068" opacity="0.06" />
                    </g>

                    {/* ============ SPARKLES ============ */}
                    <g fill="#F3D068" className="ps-twinkle">
                      <polygon points="60,80 63,86 69,89 63,92 60,98 57,92 51,89 57,86" opacity="0.9" />
                      <polygon points="330,120 332,124 336,126 332,128 330,132 328,128 324,126 328,124" opacity="0.8" />
                      <polygon points="55,340 58,346 64,349 58,352 55,358 52,352 46,349 52,346" opacity="0.85" />
                      <polygon points="345,380 347,384 351,386 347,388 345,392 343,388 339,386 343,384" opacity="0.7" />
                      <polygon points="200,60 202,64 206,66 202,68 200,72 198,68 194,66 198,64" opacity="0.8" />
                      <polygon points="270,440 272,444 276,446 272,448 270,452 268,448 264,446 268,444" opacity="0.6" />
                    </g>
                    <g fill="#F3D068">
                      <circle cx="100" cy="110" r="1.4" opacity="0.7" />
                      <circle cx="310" cy="200" r="1.2" opacity="0.6" />
                      <circle cx="150" cy="420" r="1.5" opacity="0.8" />
                      <circle cx="250" cy="390" r="1" opacity="0.5" />
                      <circle cx="80" cy="260" r="1" opacity="0.5" />
                    </g>
                  </svg>

                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: "linear-gradient(115deg, transparent 42%, rgba(255,255,255,0.05) 50%, transparent 58%)"
                  }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SEZIONI ============ */}
      <section id="sezioni" className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <SectionHead
          eyebrow="Sezioni"
          title="Scegli la tua sezione"
          subtitle="Ogni sezione contiene i prodotti giusti. Clicca ed entra dove vuoi."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-10">
          {CATS.map((c, idx) => (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="card-tcg group p-6 md:p-7 flex flex-col justify-between min-h-[220px] relative overflow-hidden"
            >
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#C9A227]/5 group-hover:bg-[#C9A227]/10 transition-colors" />
              <div className="relative">
                <div className="text-[10px] font-mono-ps text-[#C9A227] tracking-[0.3em]">{c.tag}</div>
                <div className="mt-1 text-[10px] font-mono-ps text-[#6B7280] tracking-widest">
                  {String(idx + 1).padStart(2, "0")} / {String(CATS.length).padStart(2, "0")}
                </div>
              </div>
              <div className="relative">
                <h3 className="font-display text-2xl md:text-3xl font-extrabold text-white group-hover:text-[#C9A227] transition-colors leading-tight">
                  {c.title}
                </h3>
                <p className="text-sm text-[#9CA3AF] mt-2 leading-relaxed">{c.desc}</p>
                <div className="mt-5 flex items-center gap-2 text-xs font-mono-ps uppercase tracking-widest text-[#C9A227]">
                  Entra nella sezione <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ TEMPI DI CONSEGNA ============ */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-20 border-t border-[#C9A227]/15">
        <SectionHead
          eyebrow="Tempi di consegna"
          title="Trasparenza sui tempi"
          subtitle="Vogliamo che tu sappia esattamente quando riceverai i tuoi prodotti prima ancora di ordinarli."
          icon={<Clock size={16} />}
        />

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="border border-[#C9A227]/20 rounded-sm p-6 bg-[#0b0b0d]">
            <div className="text-xs font-mono-ps uppercase tracking-widest text-[#C9A227] flex items-center gap-2">
              🇯🇵 Prodotti giapponesi
            </div>
            <div className="font-display text-3xl font-extrabold text-white mt-3">
              15-20 <span className="text-lg text-[#9CA3AF] font-normal">giorni lavorativi</span>
            </div>
            <p className="text-sm text-[#D1D5DB] mt-3 leading-relaxed">
              Vale sia per <span className="text-white font-semibold">One Piece JP</span> che per <span className="text-white font-semibold">Pokémon JP</span>:
              i tempi si contano dal momento dell'ordine, poiché i prodotti giapponesi vengono ordinati
              direttamente dal Giappone da fornitori selezionati.
            </p>
            <p className="text-xs text-[#9CA3AF] mt-3 leading-relaxed">
              <span className="text-[#C9A227] font-semibold">Eccezione:</span> per i preorder JP con data di uscita indicata,
              fa fede la data ufficiale del prodotto scritta nella scheda.
            </p>
          </div>

          <div className="border border-[#C9A227]/20 rounded-sm p-6 bg-[#0b0b0d]">
            <div className="text-xs font-mono-ps uppercase tracking-widest text-[#C9A227] flex items-center gap-2">
              🇬🇧🇮🇹 One Piece EN + Pokémon IT
            </div>
            <div className="font-display text-3xl font-extrabold text-white mt-3">
              5-7 <span className="text-lg text-[#9CA3AF] font-normal">giorni lavorativi</span>
            </div>
            <p className="text-sm text-[#D1D5DB] mt-3 leading-relaxed">
              Per i prodotti <span className="text-white font-semibold">già disponibili</span> a magazzino,
              consegna in 5-7 giorni lavorativi dal momento dell'ordine.
            </p>
            <p className="text-xs text-[#9CA3AF] mt-3 leading-relaxed">
              <span className="text-[#C9A227] font-semibold">Preorder EN in allocazione:</span> fa fede la data di uscita ufficiale indicata sulla scheda prodotto.
            </p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <Link to="/spedizioni" data-testid="home-shipping-link" className="btn-ghost-gold">
            Info spedizioni &amp; corrieri <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ============ RICHIESTE PRODOTTI ============ */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-20 border-t border-[#C9A227]/15">
        <div className="border border-[#C9A227]/25 bg-gradient-to-br from-[#0b0b0d] to-[#111] p-8 md:p-12 rounded-sm relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-30 pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="eyebrow flex items-center gap-2"><Sparkles size={14} /> Richieste prodotti</div>
              <h3 className="font-display text-3xl md:text-4xl font-extrabold mt-3">Non trovi quello che cerchi?</h3>
              <p className="text-[#9CA3AF] mt-3 leading-relaxed">
                Dicci cosa stai cercando — carta singola, box giapponese, edizione promo — e proveremo a trovarlo per te.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link to="/richieste" data-testid="product-request-cta" className="btn-gold">
                Fai una richiesta <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY WHATSAPP ============ */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-20 border-t border-[#C9A227]/15">
        <div className="border border-[#C9A227]/25 bg-[#0b0b0d] rounded-sm relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-20 pointer-events-none" />
          <div className="relative p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="eyebrow flex items-center gap-2">
                <MessageCircle size={14} /> Community WhatsApp
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-extrabold mt-3 leading-tight">
                Entra nel <span className="text-[#C9A227]">gruppo WhatsApp</span> PULL STATION
              </h3>
              <p className="text-[#9CA3AF] mt-4 leading-relaxed max-w-lg">
                Aggiornamenti in tempo reale su nuovi preorder, restock, prodotti esclusivi e occasioni riservate ai membri della community.
                Un solo canale, nessuno spam.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-[#D1D5DB]">
                <li className="flex items-center gap-2"><span className="text-[#C9A227]">▸</span> Anteprima drop &amp; preorder</li>
                <li className="flex items-center gap-2"><span className="text-[#C9A227]">▸</span> Restock e occasioni flash</li>
                <li className="flex items-center gap-2"><span className="text-[#C9A227]">▸</span> Comunicazioni ordini e allocazioni</li>
              </ul>
            </div>
            <div className="flex md:justify-end">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-join"
                className="btn-gold text-base"
              >
                <MessageCircle size={18} /> Unisciti al gruppo
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Trust = ({ icon, label }) => (
  <div className="flex items-start gap-2 text-xs text-[#9CA3AF] leading-tight">
    <span className="text-[#C9A227] mt-0.5">{icon}</span>
    <span>{label}</span>
  </div>
);

export const SectionHead = ({ eyebrow, title, subtitle, icon }) => (
  <div>
    <div className="eyebrow flex items-center gap-2">{icon}{eyebrow}</div>
    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-3">{title}</h2>
    {subtitle && <p className="text-[#9CA3AF] mt-3 max-w-2xl">{subtitle}</p>}
  </div>
);
